import { MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useEffect } from "react"
import { ControllerRenderProps, useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { useApplications } from "../../../../hooks/useApplications"
import { digitalManagerApi } from "../../../../services/api"
import {
  useApplicationSelectRenderer,
  useFeedbackTopicPriorityField,
} from "../../../components/Helpers"
import { TopicForm } from "../../../components/forms/TopicForm"
import { AccessLevel, DimaFeatureShortNameEnum } from "../../../models/Authorization"
import { FeedbackTopicPost } from "../../../models/FeedbackTopic"
import { ControllerFieldRendererProps } from "../../../types/reactHookForm"

const validationSchema = z.object({
  applicationId: z.string().min(1, "Application is required"),
  name: z.string().min(1, "Name is required"),
  priority: z.number().min(0, "Priority is required"),
})

export function FeedbackTopicCreate() {
  const { applicationsList, isLoadingApplications } = useApplications(
    DimaFeatureShortNameEnum.FB,
    AccessLevel.Write
  )
  const navigate = useNavigate()
  const returnURL = `/feedbacks#topics`

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm<FeedbackTopicPost>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  useEffect(() => {
    reset({
      applicationId: "",
      name: "",
      priority: 0,
    })
  }, [reset])

  const onSubmit = useCallback(
    (values: FeedbackTopicPost) =>
      digitalManagerApi
        .post(`/feedbackTopics`, {
          applicationId: values.applicationId,
          name: values.name,
          priority: values.priority,
        })
        .then((res) => {
          if (res.status === StatusCodes.CREATED) {
            toast.success("Feedback topic created")
            navigate(returnURL)
          }
        })
        .catch(() => {
          toast.error("Cannot create feedback topic")
        }),
    [navigate, returnURL]
  )

  const handleApplicationSelectChange = useCallback(
    (
        field: ControllerRenderProps<
          { applicationId: string } & FeedbackTopicPost,
          "applicationId"
        >
      ) =>
      (values: Array<string | { id: string }>) => {
        const first = Array.isArray(values) && values.length > 0 ? values[0] : ""
        const value = typeof first === "string" ? first : first?.id ?? ""
        field.onChange(value)
      },
    []
  )

  const renderApplicationIdSelect = useApplicationSelectRenderer({
    applicationsList,
    errors,
    handleApplicationSelectChange,
    isLoadingApplications,
  })

  const handleNameFieldChange = useCallback(
    (
        field: ControllerRenderProps<
          { applicationId: string } & FeedbackTopicPost,
          "name"
        >
      ) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const renderTextFieldName = useCallback(
    ({
      field,
    }: ControllerFieldRendererProps<
      { applicationId: string } & FeedbackTopicPost,
      "name"
    >) => (
      <MUITextField
        id="name"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleNameFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.name?.message === "string"
            ? { name: { message: errors.name.message } }
            : undefined
        }
      />
    ),
    [errors.name, handleNameFieldChange]
  )

  const renderPriorityTextField = useFeedbackTopicPriorityField<FeedbackTopicPost>({
    errors,
  })

  return (
    <TopicForm
      returnURL={returnURL}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
      includePriority
      control={control}
      errors={errors}
      renderApplicationSelect={renderApplicationIdSelect}
      renderNameField={renderTextFieldName}
      renderPriorityField={renderPriorityTextField}
    />
  )
}
